import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

export const Route = createFileRoute('/unsubscribe')({
  component: UnsubscribePage,
  validateSearch: (s: Record<string, unknown>) => ({ token: (s.token as string) ?? '' }),
})

type State = 'loading' | 'valid' | 'already' | 'invalid' | 'success' | 'error'

function UnsubscribePage() {
  const { token } = Route.useSearch()
  const [state, setState] = useState<State>('loading')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!token) {
      setState('invalid')
      return
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}))
        if (!r.ok) return setState('invalid')
        if (data.valid) return setState('valid')
        if (data.reason === 'already_unsubscribed') return setState('already')
        return setState('invalid')
      })
      .catch(() => setState('error'))
  }, [token])

  const confirm = async () => {
    setSubmitting(true)
    try {
      const r = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await r.json().catch(() => ({}))
      if (!r.ok) return setState('error')
      if (data.success) return setState('success')
      if (data.reason === 'already_unsubscribed') return setState('already')
      setState('error')
    } catch {
      setState('error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-8 text-center shadow-sm">
        <h1 className="text-2xl font-semibold text-foreground">Cancelar inscrição</h1>
        <div className="mt-4 text-sm text-muted-foreground">
          {state === 'loading' && <p>Validando link…</p>}
          {state === 'invalid' && <p>Link inválido ou expirado.</p>}
          {state === 'error' && <p>Algo deu errado. Tente novamente em alguns minutos.</p>}
          {state === 'already' && <p>Você já está descadastrado(a). Não enviaremos mais e-mails.</p>}
          {state === 'success' && (
            <p>Pronto! Você foi removido(a) da nossa lista de e-mails.</p>
          )}
          {state === 'valid' && (
            <>
              <p>Confirme que deseja parar de receber nossos e-mails.</p>
              <button
                onClick={confirm}
                disabled={submitting}
                className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {submitting ? 'Processando…' : 'Confirmar cancelamento'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
